<template>
  <div id="page-invoices-list">
    <vx-card
      ref="filterCard"
      :title="$t('invoices.filters')"
      class="invoice-list-filters mb-8"
      @refresh="resetColFilters"
      refreshContentAction
      collapseAction
    >
      <div class="vx-row">
        <div class="vx-col md:w-2/5 w-full">
          <label class="text-sm opacity-75">{{ $t('entity.states.states') }}</label>
          <v-select
            :options="stateOptions"
            :clearable="false"
            taggable
            multiple
            :placeholder="$t('forms.select_option')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="stateFilter"
            class="mb-4 md:mb-0"
          />
        </div>
      </div>
    </vx-card>

    <vs-table
      ref="table"
      :data="invoicesData"
      :total="limit"
      @selected="handleSelected"
      :sst="true"
      @sort="handleSort"
      class="card-table"
    >
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
        <div>
          <!-- ITEMS PER PAGE -->
          <vs-dropdown vs-trigger-click class="cursor-pointer" v-if="invoicesDataCount">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2">
                {{ (currentPage * limit - (limit - 1)) | persian_digits }} {{ $t('forms.to') }}
                {{
                  invoicesDataCount - currentPage * limit > 0
                    ? currentPage * limit
                    : invoicesDataCount | persian_digits
                }}
                {{ $t('forms.from') }} {{ invoicesDataCount | persian_digits }}
              </span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="paginationSetPageSize(5)">
                <span>{{ 5 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(10)">
                <span>{{ 10 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(50)">
                <span>{{ 50 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(100)">
                <span>{{ 100 | persian_digits }}</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>

      <template slot="thead">
        <vs-th>{{ $t('users.user') }}</vs-th>
        <vs-th>{{ $t('invoices.provider') }}</vs-th>
        <vs-th>{{ $t('invoices.items') }}</vs-th>
        <vs-th>{{ $t('invoices.total_price') }}</vs-th>
        <vs-th>{{ $t('services.service_status') }}</vs-th>
        <vs-th sort-key="state">{{ $t('entity.state') }}</vs-th>
        <vs-th sort-key="created_at">{{ $t('invoices.created_at') }}</vs-th>
        <vs-th>{{ $t('forms.actions') }}</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody id="table-body" v-if="data.length">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              <p class="mr-2" v-if="tr.request.data">
                {{ $t(`users.genders.name.${tr.request.data.gender}`) }} {{ tr.request.data.firstName }}
                {{ tr.request.data.lastName }}
              </p>
              <small v-if="tr.request.data">{{ tr.request.data.age | age_range }} </small>
            </vs-td>
            <vs-td>
              <p class="mr-2" v-if="tr.request.details">
                {{ $t(`users.genders.name.${tr.request.details.employeeUser.details.gender}`) }}
                {{ tr.request.details.employeeUser.details.firstName }}
                {{ tr.request.details.employeeUser.details.lastName }}
              </p>
            </vs-td>
            <vs-td>
              <ul>
                <li v-for="(item, index) in tr.items.slice(0, 3)" :key="index">
                  {{ item.count | persian_digits }} {{ $t(`units.${item.unit}`) }}
                  {{ item.service_name }}
                </li>
                <li v-if="tr.items.length > 3">
                  {{ $t('invoices.items_more', { count: tr.items.length - 3 }) | persian_digits }}
                </li>
              </ul>
            </vs-td>
            <vs-td>
              {{ tr.totalPrice | toman }}
            </vs-td>
            <vs-td>
              <cell-badges v-if="tr.request.data" :data="tr.request.data.status" local="services.status." />
            </vs-td>
            <vs-td> <cell-badges :data="tr.state" local="invoices.states." /> </vs-td>
            <vs-td>{{ tr.created_at | datetime }}</vs-td>
            <vs-td>
              <vs-button
                v-if="tr.state === 'pending'"
                class="ml-auto"
                color="success"
                type="border"
                size="small"
                @click.stop="editInvoice(tr.id)"
              >
                {{ $t('forms.edit') }}
              </vs-button>

              <vs-button
                v-if="tr.state === 'cash_unpaid'"
                color="success"
                type="border"
                size="small"
                :id="`button-pay-${tr.id}`"
                class="vs-con-loading__container mx-2"
                :disabled="payBtnDisable"
                @click.stop="payInvoice(tr)"
              >
                {{ $t('invoices.pay') }}
              </vs-button>
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>
    <!-- PAGINATION -->
    <div>
      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
  </div>
</template>

<script>
import CellBadges from '@/views/components/common/table-cells/CellBadges'
import modulePayping from '@/store/payping/modulePayping'

export default {
  name: 'ManagerInvoiceList',
  components: { CellBadges },
  data() {
    return {
      invoicesData: [],
      // Filter Options
      stateFilter: [],

      query: {},
      sort: {},
      limit: 10,
      skip: 0,

      payBtnDisable: false
    }
  },
  watch: {
    stateFilter(obj) {
      this.setColumnFilter('state', obj)
    },
    currentPage() {
      this.updateTable()
    }
  },
  computed: {
    stateOptions() {
      return this.$store.getters['enum/selectOptions']('InvoiceState', 'invoices.states')
    },
    invoicesDataCount() {
      return this.$store.state.invoice.invoicesCount
    },
    totalPages() {
      return Math.ceil(this.invoicesDataCount / this.limit)
    },
    currentPage: {
      get() {
        return Math.ceil(this.skip / this.limit + 1)
      },
      set(val) {
        this.skip = (val - 1) * this.limit
        this.updateTable()
      }
    }
  },
  methods: {
    updateTable() {
      this.$vs.loading({
        scale: 0.6
      })
      this.$store
        .dispatch('invoice/fetchInvoices', {
          query: {
            ...this.query,
            groups: this.$store.state.AppActiveUser.id,
            sort: { created_at: -1 }
          },
          skip: this.skip,
          limit: this.limit
        })
        .then(res => {
          const data = JSON.parse(JSON.stringify(res.data))
          if (data.length === 0) {
            this.$vs.loading.close()
            this.invoicesData = data
          }
          data.forEach(invoice => {
            invoice.request.data = {}
            this.$store
              .dispatch('request/fetchHealthRequestById', invoice.request.id)
              .then(res => {
                invoice.request.data = JSON.parse(JSON.stringify(res.data))

                this.$store
                  .dispatch('request/fetchRequestDetailsBySubject', {
                    id: res.data.id,
                    subject: res.data.subject,
                    category: res.data.category
                  })
                  .then(r => {
                    invoice.request.details = r.data
                  })
                  .finally(() => {
                    if (res.data.id === data[data.length - 1].request.id) {
                      this.invoicesData = data
                      this.$vs.loading.close()
                    }
                  })
              })
              .catch(() => this.$vs.loading.close())
          })
        })
        .catch(() => this.$vs.loading.close())
      this.$store.dispatch('invoice/countInvoices', {
        query: {
          ...this.query,
          groups: this.$store.state.AppActiveUser.id
        }
      })
    },
    paginationSetPageSize(items) {
      this.limit = items
      this.updateTable()
    },
    setColumnFilter(column, val) {
      if (val.length > 0) {
        const filter = []
        let i = null
        for (i in val) {
          filter.push(val[i].value ? val[i].value : val[i].label)
        }
        this.query[column] = { $in: filter }
      } else {
        delete this.query[column]
      }
      this.currentPage = 1
      this.updateTable()
    },
    resetColFilters() {
      this.stateFilter = []
      this.query = {}
      this.currentPage = 1
      this.updateTable()
      this.$refs.filterCard.removeRefreshAnimation()
    },
    handleSort(col, active) {
      if (col === null) return
      if (active !== null) {
        this.sort[col] = active === 'asc' ? 1 : -1
      } else {
        this.sort = {}
      }
      this.currentPage = 1
      this.updateTable()
    },
    handleSelected(invoice) {
      this.$router.push({ name: 'business-invoice-view', params: { id: invoice.id } })
    },
    editInvoice(id) {
      this.$router.push({ name: 'business-invoice-edit', params: { id } })
    },
    payInvoice(invoice) {
      this.payBtnDisable = true

      this.$vs.loading({
        background: 'success',
        container: `#button-pay-${invoice.id}`,
        color: '#fff',
        scale: 0.45
      })

      this.$store
        .dispatch('payping/fetchPaymentIpg', invoice.paypingPaymentId)
        .then(response => {
          window.location = response.data
        })
        .finally(() => {
          this.payBtnDisable = false
          this.$vs.loading.close(`#button-pay-${invoice.id} > .con-vs-loading`)
        })
    }
  },
  created() {
    if (!modulePayping.isRegistered) {
      this.$store.registerModule('payping', modulePayping)
      modulePayping.isRegistered = true
    }
    if (this.$route.query.state) {
      if (Array.isArray(this.$route.query.state)) {
        this.stateFilter = this.$route.query.state.map(state => {
          return {
            value: state,
            label: this.$t(`invoices.states.${state}`)
          }
        })
      } else {
        this.stateFilter = [
          {
            value: this.$route.query.state,
            label: this.$t(`invoices.states.${this.$route.query.state}`)
          }
        ]
      }
    }
    this.updateTable()
  }
}
</script>
